import { useMutation, useQuery } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import { DSButton } from "@components/design-system/molecules";
import {
  faSpinner,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { REVOKE_AGENT_TOOL_PERMISSION_GRANT } from "@graphql/mutations/RevokeAgentToolPermissionGrant";
import { GET_AGENT_TOOL_PERMISSION_GRANTS } from "@graphql/queries/AgentToolPermissionGrants";
import { useWatchUserWsEvent } from "@hooks";
import { useState } from "react";

const formatGrantDate = (value) => {
  if (!value) return "Never used";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never used";
  return `Last used ${date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })}`;
};

const CompanionPermissions = () => {
  const { data, loading, error, refetch } = useQuery(
    GET_AGENT_TOOL_PERMISSION_GRANTS,
    { fetchPolicy: "cache-and-network" },
  );
  const [revokeGrant] = useMutation(
    REVOKE_AGENT_TOOL_PERMISSION_GRANT,
  );
  const [revokingUuid, setRevokingUuid] = useState(null);

  const grants = data?.agentToolPermissionGrants ?? [];

  useWatchUserWsEvent({
    onMessage: refetch,
    eventName: "user_update",
  });

  const handleRevoke = async (uuid) => {
    setRevokingUuid(uuid);
    try {
      await revokeGrant({ variables: { uuid } });
      await refetch();
      alert("success", "Permission revoked");
    } catch (e) {
      console.error("Failed to revoke Companion permission", e);
      alert(
        "error",
        "Failed to revoke permission. Please try again.",
      );
    } finally {
      setRevokingUuid(null);
    }
  };

  if (loading && grants.length === 0) {
    return (
      <div className="py-sm text-sm text-secondary">
        Loading permissions…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-sm py-sm">
        <span className="text-sm text-danger">
          Could not load permissions.
        </span>
        <DSButton
          variant="secondary"
          size="sm"
          onClick={() => refetch()}
          dataTestId="companion-permissions-retry"
        >
          Retry
        </DSButton>
      </div>
    );
  }

  if (grants.length === 0) {
    return (
      <div
        className="py-sm text-sm text-secondary"
        data-testid="companion-permissions-empty"
      >
        No saved permissions.
      </div>
    );
  }

  return (
    <div
      className="divide-y divide-secondary"
      data-testid="companion-permissions-list"
    >
      {grants.map((grant) => {
        const isRevoking = revokingUuid === grant.uuid;
        return (
          <div
            key={grant.uuid}
            className="flex items-center justify-between gap-md py-md"
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-primary">
                {grant.displayName}
              </div>
              <div className="text-xs text-secondary">
                {grant.toolName}.{grant.toolAction} ·{" "}
                {formatGrantDate(grant.lastUsedAt)}
              </div>
            </div>
            <DSButton
              variant="cancel"
              size="sm"
              disabled={isRevoking}
              onClick={() => handleRevoke(grant.uuid)}
              dataTestId={`companion-permission-revoke-${grant.uuid}`}
            >
              <span className="inline-flex items-center gap-xs">
                <FontAwesomeIcon
                  icon={isRevoking ? faSpinner : faTrash}
                  className={isRevoking ? "animate-spin" : ""}
                />
                Revoke
              </span>
            </DSButton>
          </div>
        );
      })}
    </div>
  );
};

export default CompanionPermissions;
